// src/lib/janetSourceCitation.js — the ONE validated-reference contract for
// Janet's answer source citations (issue #254 consolidated review round 2
// finding 3: "validate the complete reference contract on server and
// restored client payloads: checked route kind must match source type,
// checked ID must be non-null and match the source ID, email date must be
// valid, and required existing-record validation must actually succeed").
//
// Shared by the server (api/_lib/janet-chat-citations.js, which builds the
// payload from trusted tool results during the gather loop) and the client
// (JanetChatPanel's AnswerSources, which re-validates a payload restored
// from a reloaded topic's persisted `payload` JSONB — a corrupted/tampered
// stored path must never degrade into a citation that renders and
// navigates to the WRONG destination). PURE — no Supabase import — so both
// sides read the same contract, the same way janetNavigation.js itself is
// shared between api/ and src/.
//
// The path used to navigate or open a citation is ALWAYS re-derived from
// `type`+`id` here, through `sourceCitationPath` — a stored `path` field
// (from a persisted payload) is never trusted directly, even when present;
// it is historical display data only. This is what closes the gap where a
// persisted source carrying `type:'email', id:<realUUID>, path:'/settings'`
// would otherwise render a citation that opens Settings instead of the
// message it claims to cite.
import { checkNavPath } from './janetNavigation.js'

const SOURCE_KINDS = new Set(['contact', 'email'])

// The canonical path for a source — the ONLY path ever used to navigate or
// render a citation.
export function sourceCitationPath(type, id) {
  if (type === 'contact') return `/contacts/${id}`
  if (type === 'email')   return `/inbox?email=${id}`
  return null
}

function isValidDate(value) {
  if (!value) return false
  const d = new Date(value)
  return !Number.isNaN(d.getTime())
}

// Validates one candidate source against the complete reference contract.
// Returns a normalized `{ type, id, label, date, path, table }` record on
// success, or null — never a half-valid object, and never the caller's own
// (possibly tampered) `path` or id casing.
//
// Four checks, each closing a reproduced gap:
// 1. The route checkNavPath resolves from the CANONICAL path (derived from
//    type+id, never the stored path) must exist at all.
// 2. Its KIND must agree with the source's declared type — an id that
//    isn't shaped like the type it claims (e.g. a non-UUID "email" id, or
//    one containing a `/`) resolves to a generic page/no-match, never
//    silently degrades into a lesser, unchecked citation for that page.
// 3. Its ID must be non-null and agree with the source's own id — closes
//    the "a stored path naming a different record is trusted anyway" gap.
// 4. An email citation's date must be a real, parseable date — a dated-
//    message citation is meaningless (and was the un-openable "no date"
//    case) without one.
export function validateSourceCitation(candidate) {
  const type = candidate?.type
  const rawId = candidate?.id
  if (!SOURCE_KINDS.has(type) || !rawId || !candidate?.label) return null

  const path = sourceCitationPath(type, String(rawId))
  if (!path) return null
  const check = checkNavPath(path)
  if (!check.ok) return null
  if (check.kind !== type) return null
  if (!check.id || check.id !== String(rawId).toLowerCase()) return null

  let date = null
  if (type === 'email') {
    if (!isValidDate(candidate.date)) return null
    date = candidate.date
  }

  return {
    type,
    id: check.id,
    label: String(candidate.label).slice(0, 160),
    date,
    path: check.path,
    table: check.table,
  }
}
